/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment'
import { SESSION_NAME_LOCAL, useAppContext } from 'src/helpers/app'
import { EVENT_QUIZ, QuizQuestionContext, SCREEN_QUIZ_QUESTION } from './contextAPI'
import { PATH_URL, getQueryParameters, splitFullName } from 'src/helpers'
import { EVENT_LOCATION_TYPE, IStringDynamicPrototype } from 'src/types'
import { identifyLeadIfPresent } from 'src/helpers/segment'

type ExternalQuizData = {
  yourName: string
  partnerName: string
  email: string
  phone: string
  eventDate: string
  planningStage: string
  location: string
  referralSource?: string
}

const TEMP_SESSION_DATA_KEY = 'tempPoppySessionData'

// The final submit builds its `colorPaletteId` from this localStorage entry, so it is
// the only reliable signal that the palette step has actually been answered.
const hasSelectedColorPalette = (): boolean => {
  try {
    const tempSessionData = JSON.parse(localStorage.getItem(TEMP_SESSION_DATA_KEY) || '{}')
    return Boolean(tempSessionData?.colorPalette?.id)
  } catch (err) {
    return false
  }
}

// The availability step reads its date back out of this entry and is the only thing
// that writes it, so a prefilled event date has to be seeded here or she is asked
// again for a date she already gave us.
const persistPrefilledEventDate = (eventDate: string) => {
  const parsed = moment(eventDate)
  if (!parsed.isValid()) return

  try {
    const tempSessionData = JSON.parse(localStorage.getItem(TEMP_SESSION_DATA_KEY) || '{}')
    const quizResponses: { key: string; values: string[] | string }[] = Array.isArray(tempSessionData.quizResponses)
      ? tempSessionData.quizResponses
      : []
    const values = parsed.format('YYYY-MM-DD')
    const eventDateIndex = quizResponses.findIndex((response) => response?.key === EVENT_QUIZ.EVENT_DATE)

    if (eventDateIndex > -1) {
      quizResponses[eventDateIndex] = { ...quizResponses[eventDateIndex], values }
    } else {
      quizResponses.push({ key: EVENT_QUIZ.EVENT_DATE, values })
    }

    localStorage.setItem(TEMP_SESSION_DATA_KEY, JSON.stringify({ ...tempSessionData, quizResponses }))
  } catch (err) {
    // a corrupt entry is rebuilt by the availability step
  }
}

export const useExternalQuiz = () => {
  const history = useHistory()
  const { storeURLParameters, setStoreURLParameters, handleChangeAppData } = useAppContext()
  const { setQuizData, setQuizScreen, setRecordResponse } = useContext(QuizQuestionContext)
  const quizDataString = atob((storeURLParameters?.quizData as string) || '')

  const gTagSetUserData = useCallback((param: any) => {
    const report = (window as { [key: string]: any })['gtag']
    if (report) {
      report('set', 'user_data', param)
    }
  }, [])

  const recordQuiz = useCallback(async (quizData: ExternalQuizData) => {
    // record quiz
    try {
      handleChangeAppData({ countLoading: 1 })
      const [firstName, lastName] = splitFullName(quizData.yourName)
      const [partnerFirstName, partnerLastName] = splitFullName(quizData.partnerName)
      const recordResponse = await setQuizData({
        weddingDate: {
          fullName: quizData.yourName,
          partnerName: quizData.partnerName,
          weddingDate: quizData.eventDate,
          firstName,
          lastName,
          partnerFirstName,
          partnerLastName,
          emailAddress: quizData.email,
          phoneNumber: quizData.phone,
        },
        location: [
          {
            type: EVENT_LOCATION_TYPE.CEREMONY,
            venue: '',
            state: '',
            streetAddress: '',
            city: '',
            zipCode: '',
            place: quizData.location,
          },
        ],
        planningStage: { stage: quizData.planningStage, vendors: {}, intent: '' },
        referralSource: quizData.referralSource,
        experiment: [],
      })

      // Mixpanel identify needs the portal / Poppy customer id, not the quiz session id.
      // TODO(mixpanel-identify): recordQuiz / last-step responses do not currently return
      // poppyCustomerId, rootPortalUserId, or customerId. Wire identifyLeadIfPresent once
      // the quiz API includes one of those fields so LP -> quiz anonymous distinct_id merges.
      identifyLeadIfPresent(recordResponse?.data)
      gTagSetUserData({
        email: quizData.email,
        phone_number: quizData.phone,
        address: {
          firstName: firstName,
          lastName: lastName,
        },
      })
      handleChangeAppData({ countLoading: -1 })
      if (recordResponse?.data) {
        setRecordResponse(recordResponse.data)
        const searchParams = getQueryParameters()

        if (recordResponse.data.validateError?.email) {
          toast.error('Email do not exist!')
          return
        }
        if (recordResponse?.data?.proposal?.hadProposal || recordResponse?.data?.hadProposal === true) {
          searchParams?.delete('quizData')
          searchParams?.delete('x4')
          let searchParamsObject: IStringDynamicPrototype = {}
          searchParamsObject = Object.fromEntries(searchParams)
          setStoreURLParameters(searchParamsObject)
          history.push({ pathname: PATH_URL.EXISTING_PROPOSAL, search: searchParams.toString() })
          setQuizScreen(SCREEN_QUIZ_QUESTION.MET_BEFORE)
          return
        }
        if (recordResponse.data.serviceable) {
          persistPrefilledEventDate(quizData.eventDate)
          searchParams?.delete('quizData')
          searchParams?.delete('x4')
          let searchParamsObject: IStringDynamicPrototype = {}
          searchParamsObject = Object.fromEntries(searchParams)
          setStoreURLParameters(searchParamsObject)
          const paletteSelected = hasSelectedColorPalette()
          history.push({
            pathname: paletteSelected ? PATH_URL.NEW_AVAILABILITY_PAGE : PATH_URL.NEW_COLOR_PALETTE_PAGE,
            search: searchParams?.toString(),
          })
          setQuizScreen(
            paletteSelected ? SCREEN_QUIZ_QUESTION.NEW_AVAILABILITY_PAGE : SCREEN_QUIZ_QUESTION.NEW_COLOR_PALETTE_PAGE
          )
        } else {
          // remove session_id after user exit => allow user can take the other quiz with new email
          localStorage.setItem(SESSION_NAME_LOCAL, '')
        }
      }
    } catch (err) {
      handleChangeAppData({ countLoading: -1 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMemo(() => {
    if (!quizDataString) return null

    console.log('external quiz data: ', quizDataString)
    try {
      const tempQuizData: ExternalQuizData = JSON.parse(quizDataString)
      const isInvalid =
        !tempQuizData.yourName ||
        !tempQuizData.partnerName ||
        !tempQuizData.email ||
        !tempQuizData.phone ||
        !tempQuizData.eventDate ||
        !tempQuizData.location ||
        !tempQuizData.planningStage

      if (isInvalid) {
        toast.error(
          'Missing data! Please include all mandatory fields: Your name, Partner name, Email, Phone, Event Date, Location, and PlanningStage.'
        )
        return null
      }
      recordQuiz(tempQuizData)
    } catch (err) {
      toast.error('Invalid data.')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizDataString])
}
